import { db } from '@/db/client';
import { updateSessionMetadataFromEvents } from '@/db/session-events';
import { SessionEventSchema, type SessionEvent } from '@/schemas';

export interface MergeResult {
  applied: number;
  replaced: number;
}

/**
 * Compare two events for last-write-wins conflict resolution.
 * Returns true if incoming event should replace existing event.
 */
function shouldReplaceEvent(existing: SessionEvent, incoming: SessionEvent): boolean {
  // If timestamps differ, newer timestamp wins
  if (incoming.timestamp !== existing.timestamp) {
    return incoming.timestamp > existing.timestamp;
  }

  // If timestamps are equal, use userId as tie-breaker (deterministic ordering)
  // Events with userId win over events without userId
  if (incoming.userId && !existing.userId) {
    return true;
  }
  if (!incoming.userId && existing.userId) {
    return false;
  }
  if (incoming.userId && existing.userId) {
    // Both have userId - use lexicographic comparison for deterministic ordering
    return incoming.userId > existing.userId;
  }

  // Both have no userId - use event ID as final tie-breaker
  return incoming.id > existing.id;
}

export async function mergeRemoteEvents(incoming: SessionEvent[]): Promise<MergeResult> {
  if (incoming.length === 0) return { applied: 0, replaced: 0 };
  const validated = incoming.map((e) => SessionEventSchema.parse(e));
  let applied = 0;
  let replaced = 0;
  const sessionId = incoming[0].sessionId;
  await db.transaction('rw', db.sessionEvents, db.sessionMetadata, async () => {
    for (const event of validated) {
      const existing = await db.sessionEvents.get(event.id);
      if (existing) {
        // Conflict resolution: last-write-wins (timestamp + userId)
        if (shouldReplaceEvent(existing, event)) {
          await db.sessionEvents.put(event);
          replaced += 1;
        }
        // Otherwise keep existing event
      } else {
        await db.sessionEvents.add(event);
        applied += 1;
      }
    }
    // Update metadata with all events (including replaced ones)
    await updateSessionMetadataFromEvents(sessionId, validated);
  });

  return { applied, replaced };
}
